import { useAsyncEffect } from '@xylabs/sdk-react'
import { XyoWorldMinter, XyoWorldMinterJsonObject } from '@xyo-network/sdk-xyoworld-js'
import { PropsWithChildren, useContext, useState } from 'react'

import { useApi } from '../Api'
import { EthersContext } from '../Ethers'
import { MinterContext } from './Context'

interface Props {
  enabled?: boolean
  loadCounts?: boolean
}

export const MinterProvider: React.FC<PropsWithChildren<Props>> = (props) => {
  const enabled = true
  const api = useApi()
  const { signer, provider, walletProvider, currentNetwork } = useContext(EthersContext)

  const [minter, setMinter] = useState<XyoWorldMinter>()
  const [now, setNow] = useState<number | undefined>()
  const [refreshCount, setRefreshCount] = useState(0)
  const [webData, setWebData] = useState<XyoWorldMinterJsonObject>()

  //XyoLog.debug(`MinterProvider [api:${!!apiWrapper}, wedata: ${!!webData}]`)

  useAsyncEffect(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    async (isMounted) => {
      if (enabled && api) {
        try {
          const webData = await api?.webData.get()
          if (isMounted()) {
            setWebData(webData)
          }
        } catch (ex) {
          const error = ex as Error
          console.error(`Failed to get WebData: ${error.message ?? ex}`)
          if (isMounted()) {
            setWebData({
              auctions: {
                auctions: [],
              },
            })
          }
        }
      }
    },
    [refreshCount, api, enabled],
  )

  useAsyncEffect(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    async (isMounted) => {
      const minterAddress = currentNetwork?.addresses?.minter
      const providerToUse = provider
      if (minterAddress && providerToUse && enabled) {
        let now: number | undefined
        let minter: XyoWorldMinter | undefined = undefined
        if (currentNetwork && webData) {
          minter = XyoWorldMinter.get(currentNetwork, providerToUse, signer ?? undefined, webData)
          await minter.updateWithNewEvents()
          now = minter.now()
        }
        if (isMounted()) {
          setMinter(minter)
          setNow(now)
        }
      }
    },
    [currentNetwork, provider, walletProvider, signer, webData, refreshCount, enabled],
  )

  const refresh = async () => {
    await minter?.refresh()
    setRefreshCount(refreshCount + 1)
  }

  const update = async () => {
    await minter?.updateWithNewEvents()
    setRefreshCount(refreshCount + 1)
  }

  return (
    <MinterContext.Provider
      value={{
        minter,
        now,
        refresh,
        update,
      }}
    >
      {props.children}
    </MinterContext.Provider>
  )
}
